import _ from "lodash";

import { FilterDefinitionType, FilterType } from "@/components/@types";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { FilterOperatorRow } from "@/components/organisms/UtilityBar/FilterChip/FilterChipOperatorRow";
import {
  CHIP_WORDS_CLASS,
  toTestId,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChip.tools";
import {
  getFilterOperatorSymbol,
  normalizeFilterOperator,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import { useFilterChipMutations } from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { Button } from "@/components/ui/button";
import { ButtonGroupText } from "@/components/ui/button-group";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/components/ui/utils";

export function FilterChipOperatorSection({
  filterDefinition,
  filter,
  isLocked,
  operatorName,
  isOperatorOpen,
  setIsOperatorOpen,
  operators,
  filterIndex,
  compact = false,
}: {
  filterDefinition: FilterDefinitionType;
  filter: FilterType;
  isLocked: boolean;
  operatorName: string | undefined;
  isOperatorOpen: boolean;
  setIsOperatorOpen: (isOpen: boolean) => void;
  operators: FilterDefinitionType["operators"];
  filterIndex: number;
  compact?: boolean;
}) {
  const { updateFilterAtIndex } = useFilterChipMutations();
  const normalizedOperator = normalizeFilterOperator(filter.operator);
  const isOperatorUnset = normalizedOperator == null;
  const flatOperators = _.flatten(operators ?? []);
  const operatorSymbol = getFilterOperatorSymbol(
    filter.operator,
    flatOperators,
    operatorName,
  );

  const testId = `filter-chip-operator-${toTestId(filterDefinition?.name)}`;
  const words = isOperatorUnset ? (
    <>{compact ? "…" : "is..."}</>
  ) : compact && operatorSymbol ? (
    <>{operatorSymbol}</>
  ) : (
    <>{_.capitalize(operatorName)}</>
  );

  // Yes or no: its switch, in the group as words are.
  if (filterDefinition.type === "boolean") {
    return (
      <ButtonGroupText
        data-testid={`${testId}-boolean`}
        className={cn(CHIP_WORDS_CLASS, "bg-background")}
      >
        <Switch
          checked={filter.value === true}
          disabled={isLocked}
          data-testid={`filter-chip-switch-${toTestId(filterDefinition?.name)}`}
          onCheckedChange={(checked) => {
            if (isLocked) {
              return;
            }
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              operator: "exact" as unknown as any,
              value: checked === true,
            }));
          }}
        />
      </ButtonGroupText>
    );
  }

  if (isLocked) {
    return (
      <ButtonGroupText
        data-testid={testId}
        className={cn(CHIP_WORDS_CLASS, "text-muted-foreground")}
      >
        {words}
      </ButtonGroupText>
    );
  }

  return (
    <Popper
      anchorLayout="button-group"
      isOpenState={[isOperatorOpen, setIsOperatorOpen]}
      anchor={
        <Button
          variant="outline"
          size="sm"
          data-testid={testId}
          className={cn(isOperatorUnset && "text-muted-foreground")}
        >
          {words}
        </Button>
      }
    >
      <List
        data={operators}
        onItemClick={(operator) => {
          setIsOperatorOpen(false);
          updateFilterAtIndex(filterIndex, (current) => ({
            ...current,
            operator: operator.key as unknown as any,
            value: operator.onlyOperator ? true : null,
          }));
        }}
      >
        <List.Items>
          {(operator: any) => (
            <List.Item
              value={operator}
              data-testid={`filter-operator-option-${toTestId(operator.name)}`}
            >
              <FilterOperatorRow operator={operator} />
            </List.Item>
          )}
        </List.Items>
      </List>
    </Popper>
  );
}
